// src/api.js
import axios from 'axios';
import config from './config.js';

export const XSRF_COOKIE_NAME = 'XSRF-TOKEN';
export const XSRF_HEADER_NAME = 'X-XSRF-TOKEN';
const RESPONSE_CSRF_HEADER_NAME = 'x-csrf-token';

const api = axios.create({
  baseURL: config.API_BASE_URL,
  withCredentials: true,
  xsrfCookieName: XSRF_COOKIE_NAME,
  xsrfHeaderName: XSRF_HEADER_NAME,
});

let unauthorizedHandler = null;
let inMemoryXsrfToken = null;

const getCookie = (name) => {
  if (typeof document === 'undefined') {
    return null;
  }

  const value = document.cookie
    .split(';')
    .map((cookie) => cookie.trim())
    .find((cookie) => cookie.startsWith(`${name}=`));

  if (!value) {
    return null;
  }

  try {
    return decodeURIComponent(value.split('=')[1]);
  } catch (err) {
    return null;
  }
};

const rememberXsrfToken = (token) => {
  if (typeof token === 'string' && token.trim().length > 0) {
    inMemoryXsrfToken = token.trim();
  }
};

const extractXsrfToken = (response) => {
  const headerToken = response?.headers?.[RESPONSE_CSRF_HEADER_NAME];
  if (typeof headerToken === 'string' && headerToken.trim().length > 0) {
    return headerToken.trim();
  }

  const bodyToken = response?.data?.csrfToken;
  if (typeof bodyToken === 'string' && bodyToken.trim().length > 0) {
    return bodyToken.trim();
  }

  return null;
};

api.interceptors.request.use((request) => {
  const token = getCookie(XSRF_COOKIE_NAME) || inMemoryXsrfToken;
  if (token && request && request.headers && !request.headers[XSRF_HEADER_NAME]) {
    request.headers[XSRF_HEADER_NAME] = token;
  }
  return request;
});

api.interceptors.response.use(
  (response) => {
    rememberXsrfToken(extractXsrfToken(response));
    return response;
  },
  (error) => {
    rememberXsrfToken(extractXsrfToken(error?.response));
    const status = error?.response?.status;
    console.log('API response error status:', status);
    if ([401, 403, 419, 440].includes(status) &&
      typeof unauthorizedHandler === 'function'
    ) {
      unauthorizedHandler(error);
    }
    return Promise.reject(error);
  }
);

export const setUnauthorizedHandler = (handler) => {
  unauthorizedHandler = typeof handler === 'function' ? handler : null;
};

export default api;
